import React, { useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useHistory } from 'react-router-dom';

import { StoreContext } from '../StoreContext';
import { naturalSortByName } from '../utils/NaturalSort';
import { useCurrentFeature } from '../utils/hooks';
import { useSearchParams } from '../search_params';
import {
  View,
  useCurrentView,
  viewsAreEqual
} from '../side_panel/views/view';
import {
  deleteSharedView,
  getSharedViews,
  openConceptList,
  restoreActiveConceptList,
  saveSharedView,
  notifier
} from '../actions';
import { useDispatchUndoBanner } from '../UndoBanner';
import { ViewDetails } from '../side_panel/views/ViewDetails';
import { useSelectedSharedView } from '../side_panel/views/useSelectedSharedView';
import { useActiveConceptListHasUnsharedChanges } from '../side_panel/views/UnsharedChangesAlert';
import { Button, Checkbox, Field, Icon, Input, Select, Textarea } from '../lumen';
import { ActionModal } from './ActionModal';

export const SelectView = () => {
  const { project, sharedViews, activeConceptListName, activeConcepts } =
    useContext(StoreContext);
  const history = useHistory();
  const { searchParams } = useSearchParams();
  const [savingView, setSavingView] = useState(false);
  const dispatchUndoBanner = useDispatchUndoBanner();
  const conceptListHasUnsharedChanges =
    useActiveConceptListHasUnsharedChanges();
  const [selectedView, rememberLoadedView] = useSelectedSharedView();

  const views = Object.values(sharedViews || {}).sort(naturalSortByName);
  const match = history.location.pathname.match(
    /^\/projects\/([^/]+)\/([^/]+)/
  );
  const workspaceId = match?.[1];
  const projectId = match?.[2];
  const currentFeature = useCurrentFeature();
  // Saving only makes sense on feature pages (highlights/volume/…) — a view
  // captured on e.g. Project Settings would have no feature to navigate to.
  const canSaveView = Boolean(
    project?.permissions?.includes('write') && currentFeature
  );

  const loadView = viewObject => {
    const view = new View(viewObject);
    const previousLocation = history.location;

    const navigate = () => {
      history.push(view.toURL(projectId, workspaceId, searchParams));
      rememberLoadedView(viewObject.name);
    };

    const undoLoad = () => {
      rememberLoadedView('');
      history.replace(previousLocation);
      return Promise.resolve();
    };

    const showUndoBanner = () => {
      const overwritesConcepts =
        view.conceptListId && conceptListHasUnsharedChanges;
      dispatchUndoBanner({
        message: overwritesConcepts
          ? `Replaced view with "${viewObject.name}"\nThis also overwrote your unsaved active concepts`
          : `Replaced view with "${viewObject.name}"`,
        onUndo: overwritesConcepts
          ? () =>
              restoreActiveConceptList(
                projectId,
                activeConceptListName,
                activeConcepts
              ).then(undoLoad)
          : undoLoad,
        trackingItem: 'undo-load-view'
      });
    };

    if (view.conceptListId) {
      openConceptList(projectId, view.conceptListId)
        .then(navigate)
        .then(showUndoBanner);
    } else {
      navigate();
      showUndoBanner();
    }
  };

  const handleChange = event => {
    const value = event.target.value;
    if (!workspaceId || !projectId) return;

    getSharedViews(projectId).then(freshViews => {
      const viewObject = Object.values(freshViews || {}).find(
        v => v.name === value
      );
      if (!viewObject) {
        notifier.error(
          'Unable to load shared view. This view may have been deleted.'
        );
        return;
      }
      loadView(viewObject);
    });
  };

  return (
    <>
      <Root>
        <SelectWrap>
          <StyledSelect
            value={selectedView?.name ?? ''}
            onChange={handleChange}
            aria-label="Select a saved view"
          >
            <option value="" disabled>
              Select View ({views.length})
            </option>
            {views.map(view => (
              <option key={view.shared_view_id} value={view.name}>
                {view.name}
              </option>
            ))}
          </StyledSelect>
        </SelectWrap>
        {canSaveView && (
          <SaveButton
            type="button"
            onClick={() => setSavingView(true)}
            aria-label="Save current view"
            title="Save current view"
          >
            <Icon name="floppy-disk" size={16} />
          </SaveButton>
        )}
      </Root>
      {savingView && projectId && (
        <SaveViewModal
          projectId={projectId}
          existingViews={views}
          initialViewId={selectedView?.shared_view_id}
          onClose={() => setSavingView(false)}
          onDeleted={() => rememberLoadedView('')}
        />
      )}
    </>
  );
};

const SaveViewModal = ({
  projectId,
  existingViews,
  initialViewId,
  onClose,
  onDeleted
}) => {
  const currentView = useCurrentView();
  const dispatchUndoBanner = useDispatchUndoBanner();
  const initialView = existingViews.find(
    v => v.shared_view_id === initialViewId
  );
  const [editingId, setEditingId] = useState(initialView?.shared_view_id ?? '');
  const [name, setName] = useState(initialView?.name ?? '');
  const [note, setNote] = useState(initialView?.note ?? '');
  const [showDiff, setShowDiff] = useState(true);

  const existingView = useMemo(
    () => existingViews.find(v => v.shared_view_id === editingId),
    [existingViews, editingId]
  );

  const trimmedName = name.trim();
  const isEditMode = Boolean(existingView);

  const viewToSave = useMemo(
    () =>
      currentView.update({
        name: trimmedName,
        note,
        ...(existingView
          ? { shared_view_id: existingView.shared_view_id }
          : {})
      }),
    [currentView, trimmedName, note, existingView]
  );

  const handleDelete = () =>
    deleteSharedView(projectId, existingView.shared_view_id)
      .then(() => {
        dispatchUndoBanner({
          message: `Deleted "${existingView.name}"`,
          onUndo: () => saveSharedView(projectId, existingView),
          trackingItem: 'undo-delete-view'
        });
        onDeleted?.(existingView.name);
        onClose();
      })
      .catch(() => notifier.error("Can't delete view"));

  const handleSelectExisting = event => {
    const id = event.target.value;
    setEditingId(id);
    const target = existingViews.find(v => v.shared_view_id === id);
    setName(target?.name || '');
    setNote(target?.note || '');
  };
  const nameCollidesWithOther = existingViews.some(
    v => v.name === trimmedName && v.shared_view_id !== editingId
  );
  const viewMatchesCurrent =
    isEditMode && viewsAreEqual(currentView.view, existingView);
  const hasDifferences = isEditMode && !viewMatchesCurrent;
  const nothingChangedForEdit =
    isEditMode &&
    trimmedName === existingView.name &&
    (note || '') === (existingView.note || '') &&
    viewMatchesCurrent;
  const disabled =
    !trimmedName || nameCollidesWithOther || nothingChangedForEdit;

  const handleSave = () => {
    const payload = {
      ...viewToSave.view,
      name: trimmedName,
      note: note.trim()
    };
    return saveSharedView(projectId, payload).then(() => {
      notifier.success?.(
        isEditMode ? `Updated view "${trimmedName}"` : `Saved view "${trimmedName}"`
      );
      onClose();
    });
  };

  return (
    <ActionModal
      title={isEditMode ? 'Update view' : 'Save view'}
      submitLabel={isEditMode ? 'Update' : 'Create'}
      submittingLabel={isEditMode ? 'Updating…' : 'Creating…'}
      fallbackErrorMessage="Can't save view"
      disabled={disabled}
      onSubmit={handleSave}
      onClose={onClose}
      footerStart={
        isEditMode && (
          <Button
            variant="ghost-danger"
            onClick={handleDelete}
            title="Delete this view for all users"
          >
            <Icon name="trash" size={16} />
            Delete
          </Button>
        )
      }
    >
      <FormGrid>
        <Field
          label="Name"
          htmlFor="save-view-name"
          error={
            nameCollidesWithOther
              ? 'Another view already uses this name.'
              : undefined
          }
        >
          <Input
            id="save-view-name"
            value={name}
            autoFocus
            placeholder="Type new name of view"
            onChange={event => setName(event.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="save-view-note">
          <Textarea
            id="save-view-note"
            value={note}
            placeholder="Type new notes of view"
            onChange={event => setNote(event.target.value)}
          />
        </Field>
        {existingViews.length > 0 && (
          <Field label="Edit existing view" htmlFor="save-view-existing">
            <Select
              id="save-view-existing"
              value={editingId}
              onChange={handleSelectExisting}
            >
              <option value="">
                Select existing view ({existingViews.length})
              </option>
              {existingViews.map(v => (
                <option key={v.shared_view_id} value={v.shared_view_id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {isEditMode && (
          <>
            {hasDifferences && (
              <Checkbox
                label="Show differences between the current view and this one"
                checked={showDiff}
                onChange={() => setShowDiff(current => !current)}
              />
            )}
            <DiffBox data-test-id="save-view-diff">
              {hasDifferences && showDiff ? (
                <ViewDetails
                  comparisonView={new View(existingView)}
                  view={viewToSave}
                />
              ) : (
                <ViewDetails view={new View(existingView)} />
              )}
            </DiffBox>
          </>
        )}
      </FormGrid>
    </ActionModal>
  );
};


const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* Figma 376:1240 — 32px height, slate/300 border, 8px radius. Native
   <select> gets styled to look like the Figma dropdown; the browser
   still owns the popup for accessibility. */
const SelectWrap = styled.div`
  width: 186px;
`;

const StyledSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 32px;
  padding: 0 32px 0 12px;
  background: ${({ theme }) => theme.color.bg.default}
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M5 7.5L10 12.5L15 7.5' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")
    no-repeat right 8px center;
  border: 1px solid ${({ theme }) => theme.color.slate[300]};
  border-radius: 8px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  line-height: normal;
  color: ${({ theme }) => theme.color.slate[500]};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.primary.base};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.blue[100]};
  }
`;

/* Figma 376:1251 — 32px square icon button, slate border, 8px radius. */
const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid #bfbfbf;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.fg.muted};

  &:hover {
    background: ${({ theme }) => theme.color.slate[100]};
    color: ${({ theme }) => theme.color.fg.default};
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DiffBox = styled.div`
  max-height: 40vh;
  overflow: auto;
  padding: 12px;
  background: ${({ theme }) => theme.color.bg.subtle};
  border: 1px solid ${({ theme }) => theme.color.slate[200]};
  border-radius: ${({ theme }) => theme.radius.base};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.fg.default};
`;
